import { useEffect, useRef, useState, type CSSProperties } from 'react';
import yettiUp from '../../levels/yetti/obstacles/up.png';
import yettiBottom from '../../levels/yetti/obstacles/bottom.png';
import {
  YETTI_OVERLAY_FADE_MS,
  YETTI_OVERLAY_HOLD_MS,
  YETTI_OVERLAY_ICE_SLIDE_MS,
} from './gameplayConstants';
import { YETTI_ICE_OVERLAY_PATHS } from './yettiOverlayTargets';
import './LevelYettiOverlay.css';

type YettiPhase = 'slide' | 'hold' | 'out';

const PHASE_MS: Record<YettiPhase, number> = {
  slide: YETTI_OVERLAY_ICE_SLIDE_MS,
  hold: YETTI_OVERLAY_HOLD_MS,
  out: YETTI_OVERLAY_FADE_MS,
};

const NEXT_PHASE: Record<YettiPhase, YettiPhase | null> = {
  slide: 'hold',
  hold: 'out',
  out: null,
};

const ICE_SRC = {
  yetti_up: yettiUp,
  yetti_bottom: yettiBottom,
} as const;

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

type LevelYettiOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

export function LevelYettiOverlay({
  isPaused,
  onComplete,
}: LevelYettiOverlayProps) {
  const [phase, setPhase] = useState<YettiPhase>('slide');
  const [overlayShown, setOverlayShown] = useState(false);
  const [slideT, setSlideT] = useState(0);
  const remainingMsRef = useRef(PHASE_MS.slide);
  const slideElapsedRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setOverlayShown(true);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    if (phase === 'out') {
      setOverlayShown(false);
    }
  }, [phase]);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();
    const tick = (now: number) => {
      const dt = now - lastNow;
      lastNow = now;
      remainingMsRef.current -= dt;
      if (phase === 'slide') {
        slideElapsedRef.current = Math.min(
          YETTI_OVERLAY_ICE_SLIDE_MS,
          slideElapsedRef.current + dt,
        );
        setSlideT(
          easeOutCubic(slideElapsedRef.current / YETTI_OVERLAY_ICE_SLIDE_MS),
        );
      }
      if (remainingMsRef.current > 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      const next = NEXT_PHASE[phase];
      if (next == null) {
        onCompleteRef.current();
        return;
      }
      remainingMsRef.current = PHASE_MS[next];
      setPhase(next);
      if (next === 'hold') {
        setSlideT(1);
      }
    };
    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [phase, isPaused]);

  const t = phase === 'slide' ? slideT : 1;

  return (
    <div className="level-yetti-overlay" aria-hidden>
      {YETTI_ICE_OVERLAY_PATHS.map((path) => {
        const xPct =
          path.startXPct + (path.finishXPct - path.startXPct) * t;
        const yPct =
          path.startYPct + (path.finishYPct - path.startYPct) * t;
        return (
          <img
            key={path.id}
            className="level-yetti-overlay__ice"
            src={ICE_SRC[path.srcKey]}
            alt=""
            style={
              {
                left: `${xPct}%`,
                top: `${yPct}%`,
                width: `${path.widthPct}%`,
                opacity: overlayShown ? 1 : 0,
                transition: `opacity ${YETTI_OVERLAY_FADE_MS}ms ease-out`,
              } as CSSProperties
            }
          />
        );
      })}
    </div>
  );
}
